import { c as _c } from "react/compiler-runtime";
import { PrimaryButton } from '@systemeio/ui';
import React, { useState, type JSX } from 'react';
import CreateModal, { CreateModalProps } from 'shared/components/create-modal';
import { Loader } from 'shared/components/loader';
import { useListingFilter } from 'shared/hooks/use-listing-filter';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { CountInterface, DataWithPaginationInterface } from 'shared/types/pagination-interface';
import { twMerge } from 'tailwind-merge';
import { BaseTableProps } from './table/components/base-table';
import { AddIcon, FilterIcon } from '@systemeio/ui/icons';
export type FilterableItemType<T> = { [K in keyof T]: {
  field: K;
  onRender: (value: T[K], onChange: (value: T[K], state?: T) => void, state: T, onChangeState: (state: T) => void) => JSX.Element;
  shouldRender?: boolean;
} }[keyof T];
export type FiltererType<K> = {
  filter: K;
  setFilter: React.Dispatch<React.SetStateAction<K>>;
  defaultFilters: K;
  responseFilter?: K;
  setResponseFilter?: React.Dispatch<React.SetStateAction<K>>;
};
export interface CreateDataInterface<T> extends Omit<CreateModalProps<T>, 'afterLeave' | 'opened' | 'onClose'> {
  onOpen?: () => Promise<void>;
  testAttributePostfix?: string;
  disabled?: boolean;
  onAfterLeave?: () => void;
}
export interface FilterDataInterface<T, K> extends Partial<Pick<BaseTableProps<T>, 'paginator' | 'limiter'>> {
  filterable: FilterableItemType<K>[];
  filterer: FiltererType<K>;
  testAttributePostfix?: string;
}
export interface ListingLayoutProps<T extends DataWithPaginationInterface<T['items'][0]>, K, R> {
  data: T | undefined;
  title: string | JSX.Element;
  titleClassName?: string;
  headerClassName?: string;
  counter?: CountInterface | false;
  type: 'side' | 'compact' | 'default' | 'large' | 'vertical';
  children: JSX.Element;
  customHeaderElement?: JSX.Element;
  filterData?: FilterDataInterface<T, K>;
  createData?: CreateDataInterface<R>;
}
const ListingLayout = t0 => {
  const $ = _c(61);
  const {
    type,
    data,
    counter,
    title,
    titleClassName,
    headerClassName,
    filterData,
    createData,
    children,
    customHeaderElement
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== counter || $[2] !== createData || $[3] !== customHeaderElement || $[4] !== data || $[5] !== filterData || $[6] !== headerClassName || $[7] !== title || $[8] !== titleClassName || $[9] !== type) {
    t1 = type === "compact" && <CompactListingLayout data={data} counter={counter} title={title} titleClassName={titleClassName} headerClassName={headerClassName} filterData={filterData} createData={createData} customHeaderElement={customHeaderElement}>{children}</CompactListingLayout>;
    $[0] = children;
    $[1] = counter;
    $[2] = createData;
    $[3] = customHeaderElement;
    $[4] = data;
    $[5] = filterData;
    $[6] = headerClassName;
    $[7] = title;
    $[8] = titleClassName;
    $[9] = type;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== children || $[12] !== counter || $[13] !== createData || $[14] !== customHeaderElement || $[15] !== data || $[16] !== filterData || $[17] !== headerClassName || $[18] !== title || $[19] !== titleClassName || $[20] !== type) {
    t2 = type === "default" && <DefaultListingLayout counter={counter} data={data} title={title} titleClassName={titleClassName} headerClassName={headerClassName} filterData={filterData} createData={createData} customHeaderElement={customHeaderElement}>{children}</DefaultListingLayout>;
    $[11] = children;
    $[12] = counter;
    $[13] = createData;
    $[14] = customHeaderElement;
    $[15] = data;
    $[16] = filterData;
    $[17] = headerClassName;
    $[18] = title;
    $[19] = titleClassName;
    $[20] = type;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  let t3;
  if ($[22] !== children || $[23] !== counter || $[24] !== createData || $[25] !== customHeaderElement || $[26] !== data || $[27] !== filterData || $[28] !== headerClassName || $[29] !== title || $[30] !== titleClassName || $[31] !== type) {
    t3 = type === "large" && <LargeListingLayout counter={counter} data={data} title={title} titleClassName={titleClassName} headerClassName={headerClassName} filterData={filterData} createData={createData} customHeaderElement={customHeaderElement}>{children}</LargeListingLayout>;
    $[22] = children;
    $[23] = counter;
    $[24] = createData;
    $[25] = customHeaderElement;
    $[26] = data;
    $[27] = filterData;
    $[28] = headerClassName;
    $[29] = title;
    $[30] = titleClassName;
    $[31] = type;
    $[32] = t3;
  } else {
    t3 = $[32];
  }
  let t4;
  if ($[33] !== children || $[34] !== counter || $[35] !== createData || $[36] !== customHeaderElement || $[37] !== data || $[38] !== filterData || $[39] !== headerClassName || $[40] !== title || $[41] !== titleClassName || $[42] !== type) {
    t4 = type === "side" && <SideListingLayout counter={counter} data={data} title={title} titleClassName={titleClassName} headerClassName={headerClassName} filterData={filterData} createData={createData} customHeaderElement={customHeaderElement}>{children}</SideListingLayout>;
    $[33] = children;
    $[34] = counter;
    $[35] = createData;
    $[36] = customHeaderElement;
    $[37] = data;
    $[38] = filterData;
    $[39] = headerClassName;
    $[40] = title;
    $[41] = titleClassName;
    $[42] = type;
    $[43] = t4;
  } else {
    t4 = $[43];
  }
  let t5;
  if ($[44] !== children || $[45] !== counter || $[46] !== createData || $[47] !== customHeaderElement || $[48] !== data || $[49] !== filterData || $[50] !== headerClassName || $[51] !== title || $[52] !== titleClassName || $[53] !== type) {
    t5 = type === "vertical" && <VerticalListingLayout counter={counter} data={data} title={title} titleClassName={titleClassName} headerClassName={headerClassName} filterData={filterData} createData={createData} customHeaderElement={customHeaderElement}>{children}</VerticalListingLayout>;
    $[44] = children;
    $[45] = counter;
    $[46] = createData;
    $[47] = customHeaderElement;
    $[48] = data;
    $[49] = filterData;
    $[50] = headerClassName;
    $[51] = title;
    $[52] = titleClassName;
    $[53] = type;
    $[54] = t5;
  } else {
    t5 = $[54];
  }
  let t6;
  if ($[55] !== t1 || $[56] !== t2 || $[57] !== t3 || $[58] !== t4 || $[59] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[55] = t1;
    $[56] = t2;
    $[57] = t3;
    $[58] = t4;
    $[59] = t5;
    $[60] = t6;
  } else {
    t6 = $[60];
  }
  return t6;
};
interface ChildListingLayoutProps<T extends DataWithPaginationInterface<T['items'][0]>, K, R> extends Pick<ListingLayoutProps<T, K, R>, 'data' | 'filterData' | 'title' | 'titleClassName' | 'headerClassName' | 'createData' | 'children' | 'customHeaderElement' | 'counter'> {}
const CompactListingLayout = t0 => {
  const $ = _c(56);
  const {
    title,
    titleClassName,
    data,
    counter,
    filterData,
    createData,
    children,
    customHeaderElement
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] !== filterData) {
    t1 = {
      filterData
    };
    $[0] = filterData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState
  } = useListingFilter(t1);
  let t2;
  if ($[2] !== title || $[3] !== titleClassName) {
    t2 = () => {
      const className = `flex items-center whitespace-nowrap h-[42px] ${titleClassName || ""}`;
      return typeof title === "string" ? <h1 className={className}>{title}</h1> : <div className={className}>{title}</div>;
    };
    $[2] = title;
    $[3] = titleClassName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const WrappedTitle = t2;
  let t3;
  if ($[5] !== WrappedTitle || $[6] !== counter || $[7] !== data?.items.length || $[8] !== titleClassName) {
    t3 = data?.items.length === 0 || counter === false ? <WrappedTitle /> : <div className="flex items-center gap-1">{counter ? <h2 className={`text-text-primary whitespace-nowrap text-[22px] font-bold ${titleClassName || ""}`}>{counter.count}</h2> : <Loader type="dark" small={true} />}<WrappedTitle /></div>;
    $[5] = WrappedTitle;
    $[6] = counter;
    $[7] = data?.items.length;
    $[8] = titleClassName;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== filterData || $[11] !== isFilterShown || $[12] !== setIsFilterShown) {
    t4 = filterData && <PrimaryButton onClick={() => setIsFilterShown(!isFilterShown)} className="flex items-center lg:hidden"><FilterIcon className="h-[15px] w-[15px]" /></PrimaryButton>;
    $[10] = filterData;
    $[11] = isFilterShown;
    $[12] = setIsFilterShown;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== customHeaderElement) {
    t5 = customHeaderElement && <div className="flex justify-end lg:hidden">{customHeaderElement}</div>;
    $[14] = customHeaderElement;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== createData || $[17] !== data || $[18] !== isDefaultValuesFetching || $[19] !== setIsDefaultValuesFetching || $[20] !== t) {
    t6 = createData && <PrimaryButton disabled={!data} onClick={() => {
      if (createData.onOpen) {
        setIsDefaultValuesFetching(true);
        createData.onOpen().then(() => {
          setIsDefaultValuesFetching(false);
          setIsModalOpen(true);
        });
      } else {
        setIsModalOpen(true);
      }
    }} isFetching={isDefaultValuesFetching} className="flex lg:hidden"><AddIcon /><span className="hidden sm:flex"> {t("global.create")}</span></PrimaryButton>;
    $[16] = createData;
    $[17] = data;
    $[18] = isDefaultValuesFetching;
    $[19] = setIsDefaultValuesFetching;
    $[20] = t;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <div className="ml-auto flex gap-3">{t4}{t5}{t6}</div>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t3 || $[27] !== t7) {
    t8 = <div className="flex flex-wrap justify-between truncate lg:basis-full">{t3}{t7}</div>;
    $[26] = t3;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== data || $[30] !== filterData || $[31] !== handleFilter || $[32] !== isFilterShown || $[33] !== setTempState || $[34] !== t || $[35] !== tempState) {
    t9 = filterData && tempState && <div className={`w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible ${isFilterShown ? "flex" : "hidden lg:flex"}`}><div className="bg-brand-primary-subtle flex justify-center px-6 py-4 lg:hidden"><span className="font-inter text-text-primary text-[17px] font-bold">{t("global.filter_by")}</span></div><form className={`bg-background-secondary w-full flex-col justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0 ${isFilterShown ? "flex" : "hidden lg:flex"}`}>{filterData.filterable.map(el => el.shouldRender !== false && el.onRender(tempState[el.field], value => {
          setTempState(prev => {
            if (prev) {
              return {
                ...prev,
                [el.field]: value
              };
            }
          });
        }, tempState, newTempState => setTempState(newTempState)))}<PrimaryButton type="submit" onClick={e => {
          e.preventDefault();
          handleFilter();
        }} className="flex min-w-[126px] items-center" disabled={!data} testAttributePostfix={filterData.testAttributePostfix}><FilterIcon className="h-[15px] w-[15px]" /><span> {t("global.filter")}</span></PrimaryButton></form></div>;
    $[29] = data;
    $[30] = filterData;
    $[31] = handleFilter;
    $[32] = isFilterShown;
    $[33] = setTempState;
    $[34] = t;
    $[35] = tempState;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== customHeaderElement) {
    t10 = customHeaderElement && <div className="hidden justify-end lg:flex">{customHeaderElement}</div>;
    $[37] = customHeaderElement;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== createData || $[40] !== data || $[41] !== filterData || $[42] !== isDefaultValuesFetching || $[43] !== isModalOpen || $[44] !== setIsDefaultValuesFetching || $[45] !== setTempState || $[46] !== t) {
    t11 = createData && <><PrimaryButton disabled={createData.disabled ? createData.disabled : !data} onClick={() => {
        if (createData.onOpen) {
          setIsDefaultValuesFetching(true);
          createData.onOpen().then(() => {
            setIsDefaultValuesFetching(false);
            setIsModalOpen(true);
          });
        } else {
          setIsModalOpen(true);
        }
      }} isFetching={isDefaultValuesFetching} className="hidden lg:flex" testAttributePostfix={createData.testAttributePostfix}><AddIcon /><span> {t("global.create")}</span></PrimaryButton><CreateModal opened={isModalOpen} onClose={() => {
        setIsModalOpen(false);
      }} afterLeave={createData.onAfterLeave} {...createData} onCreate={data_0 => {
        if (filterData) {
          setTempState(filterData.filterer.defaultFilters);
          filterData.filterer.setFilter(filterData.filterer.defaultFilters);
        }
        return createData.onCreate(data_0);
      }} /></>;
    $[39] = createData;
    $[40] = data;
    $[41] = filterData;
    $[42] = isDefaultValuesFetching;
    $[43] = isModalOpen;
    $[44] = setIsDefaultValuesFetching;
    $[45] = setTempState;
    $[46] = t;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t8 || $[51] !== t9) {
    t12 = <div className="mb-5 flex flex-col justify-between gap-5 lg:flex-row lg:gap-3">{t8}{t9}{t10}{t11}</div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t8;
    $[51] = t9;
    $[52] = t12;
  } else {
    t12 = $[52];
  }
  let t13;
  if ($[53] !== children || $[54] !== t12) {
    t13 = <>{t12}{children}</>;
    $[53] = children;
    $[54] = t12;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
};
const DefaultListingLayout = t0 => {
  const $ = _c(51);
  const {
    title,
    data,
    counter,
    filterData,
    createData,
    children,
    customHeaderElement,
    headerClassName
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] !== filterData) {
    t1 = {
      filterData
    };
    $[0] = filterData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState
  } = useListingFilter(t1);
  const t2 = `flex justify-between gap-3 ${headerClassName}`;
  let t3;
  if ($[2] !== counter || $[3] !== data?.items.length || $[4] !== title) {
    t3 = data?.items.length === 0 || counter === false ? <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1> : <div className="flex items-center gap-1">{counter ? <h2 className="text-text-primary whitespace-nowrap text-[22px] font-bold">{counter.count}</h2> : <Loader type="dark" small={true} />}<h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1></div>;
    $[2] = counter;
    $[3] = data?.items.length;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== filterData || $[7] !== isFilterShown || $[8] !== setIsFilterShown) {
    t4 = filterData && <PrimaryButton onClick={() => setIsFilterShown(!isFilterShown)} className="flex items-center xl:hidden"><FilterIcon className="h-[15px] w-[15px]" /></PrimaryButton>;
    $[6] = filterData;
    $[7] = isFilterShown;
    $[8] = setIsFilterShown;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== customHeaderElement) {
    t5 = customHeaderElement && <div className="flex xl:hidden">{customHeaderElement}</div>;
    $[10] = customHeaderElement;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== createData || $[13] !== data || $[14] !== isDefaultValuesFetching || $[15] !== setIsDefaultValuesFetching || $[16] !== t) {
    t6 = createData && <PrimaryButton disabled={createData.disabled ? createData.disabled : !data} onClick={() => {
      if (createData.onOpen) {
        setIsDefaultValuesFetching(true);
        createData.onOpen().then(() => {
          setIsDefaultValuesFetching(false);
          setIsModalOpen(true);
        });
      } else {
        setIsModalOpen(true);
      }
    }} isFetching={isDefaultValuesFetching} className="flex xl:hidden" testAttributePostfix={createData.testAttributePostfix}><AddIcon /><span className="hidden sm:flex"> {t("global.create")}</span></PrimaryButton>;
    $[12] = createData;
    $[13] = data;
    $[14] = isDefaultValuesFetching;
    $[15] = setIsDefaultValuesFetching;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <div className="flex flex-wrap gap-3">{t4}{t5}{t6}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t2 || $[23] !== t3 || $[24] !== t7) {
    t8 = <div className={t2}>{t3}{t7}</div>;
    $[22] = t2;
    $[23] = t3;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== data || $[27] !== filterData || $[28] !== handleFilter || $[29] !== isFilterShown || $[30] !== setTempState || $[31] !== t || $[32] !== tempState) {
    t9 = filterData && tempState && <div className={`w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible ${isFilterShown ? "flex" : "hidden xl:flex"}`}><div className="bg-brand-primary-subtle flex justify-center px-6 py-4 lg:hidden"><span className="font-inter text-text-primary text-[17px] font-bold">{t("global.filter_by")}</span></div><form className={`bg-background-secondary w-full flex-col justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0 ${isFilterShown ? "flex" : "hidden lg:flex"}`}>{filterData.filterable.map(el => el.shouldRender !== false && el.onRender(tempState[el.field], value => {
          setTempState(prev => {
            if (prev) {
              return {
                ...prev,
                [el.field]: value
              };
            }
          });
        }, tempState, newTempState => setTempState(newTempState)))}<PrimaryButton type="submit" onClick={e => {
          e.preventDefault();
          handleFilter();
        }} className="flex items-center" disabled={!data} testAttributePostfix={filterData.testAttributePostfix}><FilterIcon className="h-[15px] w-[15px]" /><span> {t("global.filter")}</span></PrimaryButton></form></div>;
    $[26] = data;
    $[27] = filterData;
    $[28] = handleFilter;
    $[29] = isFilterShown;
    $[30] = setTempState;
    $[31] = t;
    $[32] = tempState;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== customHeaderElement) {
    t10 = customHeaderElement && <div className="hidden xl:flex">{customHeaderElement}</div>;
    $[34] = customHeaderElement;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== createData || $[37] !== data || $[38] !== isDefaultValuesFetching || $[39] !== isModalOpen || $[40] !== setIsDefaultValuesFetching || $[41] !== t) {
    t11 = createData && <><PrimaryButton disabled={!data} onClick={() => {
        if (createData.onOpen) {
          setIsDefaultValuesFetching(true);
          createData.onOpen().then(() => {
            setIsDefaultValuesFetching(false);
            setIsModalOpen(true);
          });
        } else {
          setIsModalOpen(true);
        }
      }} isFetching={isDefaultValuesFetching} className="hidden xl:flex"><AddIcon /><span> {t("global.create")}</span></PrimaryButton><CreateModal caption={createData.caption} opened={isModalOpen} requiredFields={createData.requiredFields} onClose={() => {
        setIsModalOpen(false);
      }} afterLeave={createData.onAfterLeave} defaultValues={createData.defaultValues} creatable={createData.creatable} onCreate={data_0 => createData.onCreate(data_0)} /></>;
    $[36] = createData;
    $[37] = data;
    $[38] = isDefaultValuesFetching;
    $[39] = isModalOpen;
    $[40] = setIsDefaultValuesFetching;
    $[41] = t;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t8 || $[46] !== t9) {
    t12 = <div className="mb-5 flex flex-col justify-between gap-5 lg:gap-3 xl:flex-row">{t8}{t9}{t10}{t11}</div>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t8;
    $[46] = t9;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== children || $[49] !== t12) {
    t13 = <>{t12}{children}</>;
    $[48] = children;
    $[49] = t12;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  return t13;
};
const SideListingLayout = t0 => {
  const $ = _c(40);
  const {
    title,
    data,
    counter,
    filterData,
    createData,
    children,
    customHeaderElement,
    headerClassName
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] !== filterData) {
    t1 = {
      filterData
    };
    $[0] = filterData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState
  } = useListingFilter(t1);
  let t2;
  if ($[2] !== counter || $[3] !== data?.items.length || $[4] !== headerClassName || $[5] !== title) {
    t2 = data?.items.length === 0 || counter === false ? <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1> : <div className={twMerge("flex items-center gap-1", headerClassName)}>{counter ? <h2 className="text-text-primary whitespace-nowrap text-[22px] font-bold">{counter.count}</h2> : <Loader type="dark" small={true} />}<h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1></div>;
    $[2] = counter;
    $[3] = data?.items.length;
    $[4] = headerClassName;
    $[5] = title;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== filterData || $[8] !== isFilterShown || $[9] !== setIsFilterShown) {
    t3 = filterData && <PrimaryButton onClick={() => setIsFilterShown(!isFilterShown)} className="flex items-center xl:hidden"><FilterIcon className="h-[15px] w-[15px]" /></PrimaryButton>;
    $[7] = filterData;
    $[8] = isFilterShown;
    $[9] = setIsFilterShown;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== customHeaderElement) {
    t4 = customHeaderElement && <div>{customHeaderElement}</div>;
    $[11] = customHeaderElement;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== createData || $[14] !== data || $[15] !== isDefaultValuesFetching || $[16] !== isModalOpen || $[17] !== setIsDefaultValuesFetching || $[18] !== t) {
    t5 = createData && <><PrimaryButton disabled={createData.disabled ? createData.disabled : !data} onClick={() => {
        if (createData.onOpen) {
          setIsDefaultValuesFetching(true);
          createData.onOpen().then(() => {
            setIsDefaultValuesFetching(false);
            setIsModalOpen(true);
          });
        } else {
          setIsModalOpen(true);
        }
      }} isFetching={isDefaultValuesFetching} testAttributePostfix={createData.testAttributePostfix}><AddIcon /><span className="hidden sm:flex"> {t("global.create")}</span></PrimaryButton><CreateModal caption={createData.caption} opened={isModalOpen} requiredFields={createData.requiredFields} onClose={() => {
        setIsModalOpen(false);
      }} afterLeave={createData.onAfterLeave} defaultValues={createData.defaultValues} creatable={createData.creatable} onCreate={data_0 => createData.onCreate(data_0)} /></>;
    $[13] = createData;
    $[14] = data;
    $[15] = isDefaultValuesFetching;
    $[16] = isModalOpen;
    $[17] = setIsDefaultValuesFetching;
    $[18] = t;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex gap-5 md:gap-3">{t3}{t4}{t5}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t2 || $[25] !== t6) {
    t7 = <div className="mb-5 flex justify-between">{t2}{t6}</div>;
    $[24] = t2;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== children || $[28] !== data || $[29] !== filterData || $[30] !== handleFilter || $[31] !== isFilterShown || $[32] !== setTempState || $[33] !== t || $[34] !== tempState) {
    t8 = filterData && tempState && <div className="flex flex-col gap-0 xl:flex-row xl:gap-5"><div className="flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible xl:w-[300px] xl:overflow-x-hidden"><div className={`bg-brand-primary-subtle justify-center px-6 py-4 ${isFilterShown ? "flex lg:hidden xl:flex" : "hidden xl:flex"}`}><span className="font-inter text-text-primary text-[17px] font-bold">{t("global.filter_by")}</span></div><form className={twMerge("bg-background-secondary xl:bg-background-secondary mb-5 flex w-full flex-col flex-wrap justify-start gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0 xl:mb-0 xl:flex-col xl:flex-nowrap xl:justify-end xl:px-2 xl:py-4", isFilterShown ? "flex" : "hidden xl:flex")}>{filterData.filterable.map(el => el.shouldRender !== false && el.onRender(tempState[el.field], value => {
            setTempState(prev => {
              if (prev) {
                return {
                  ...prev,
                  [el.field]: value
                };
              }
            });
          }, tempState, newTempState => setTempState(newTempState)))}<PrimaryButton type="submit" onClick={e => {
            e.preventDefault();
            handleFilter();
          }} className="flex items-center" disabled={!data} testAttributePostfix={filterData.testAttributePostfix}><FilterIcon className="h-[15px] w-[15px]" /><span> {t("global.filter")}</span></PrimaryButton></form></div><div className="xl:w-[calc(100%-300px-20px)]">{children}</div></div>;
    $[27] = children;
    $[28] = data;
    $[29] = filterData;
    $[30] = handleFilter;
    $[31] = isFilterShown;
    $[32] = setTempState;
    $[33] = t;
    $[34] = tempState;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  const t9 = !filterData && children;
  let t10;
  if ($[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t10 = <>{t7}{t8}{t9}</>;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
};
const LargeListingLayout = t0 => {
  const $ = _c(39);
  const {
    title,
    data,
    counter,
    filterData,
    createData,
    children,
    customHeaderElement
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] !== filterData) {
    t1 = {
      filterData
    };
    $[0] = filterData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState
  } = useListingFilter(t1);
  let t2;
  if ($[2] !== counter || $[3] !== data?.items.length || $[4] !== title) {
    t2 = data?.items.length === 0 || counter === false ? <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1> : <div className="flex items-center gap-1">{counter ? <h2 className="text-text-primary whitespace-nowrap text-[22px] font-bold">{counter.count}</h2> : <Loader type="dark" small={true} />}<h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1></div>;
    $[2] = counter;
    $[3] = data?.items.length;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== filterData || $[7] !== isFilterShown || $[8] !== setIsFilterShown) {
    t3 = filterData && <PrimaryButton onClick={() => setIsFilterShown(!isFilterShown)} className="flex items-center lg:hidden" testAttributePostfix={filterData.testAttributePostfix}><FilterIcon className="h-[15px] w-[15px]" /></PrimaryButton>;
    $[6] = filterData;
    $[7] = isFilterShown;
    $[8] = setIsFilterShown;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== customHeaderElement) {
    t4 = customHeaderElement && <div>{customHeaderElement}</div>;
    $[10] = customHeaderElement;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== createData || $[13] !== data || $[14] !== isDefaultValuesFetching || $[15] !== isModalOpen || $[16] !== setIsDefaultValuesFetching || $[17] !== setTempState || $[18] !== t) {
    t5 = createData && <><PrimaryButton disabled={!data} onClick={() => {
        if (createData.onOpen) {
          setIsDefaultValuesFetching(true);
          createData.onOpen().then(() => {
            setIsDefaultValuesFetching(false);
            setIsModalOpen(true);
          });
        } else {
          setIsModalOpen(true);
        }
      }} isFetching={isDefaultValuesFetching} testAttributePostfix={createData.testAttributePostfix}><AddIcon /><span className="hidden sm:flex"> {t("global.create")}</span></PrimaryButton><CreateModal caption={createData.caption} opened={isModalOpen} requiredFields={createData.requiredFields} onClose={() => {
        setIsModalOpen(false);
      }} afterLeave={createData.onAfterLeave} defaultValues={createData.defaultValues} creatable={createData.creatable} onCreate={data_0 => {
        setTempState(undefined);
        return createData.onCreate(data_0);
      }} /></>;
    $[12] = createData;
    $[13] = data;
    $[14] = isDefaultValuesFetching;
    $[15] = isModalOpen;
    $[16] = setIsDefaultValuesFetching;
    $[17] = setTempState;
    $[18] = t;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex gap-5 md:gap-3">{t3}{t4}{t5}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t2 || $[25] !== t6) {
    t7 = <div className="mb-5 flex justify-between">{t2}{t6}</div>;
    $[24] = t2;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== data || $[28] !== filterData || $[29] !== handleFilter || $[30] !== isFilterShown || $[31] !== setTempState || $[32] !== t || $[33] !== tempState) {
    t8 = filterData && tempState && <div className="flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible"><div className={twMerge("bg-brand-primary-subtle justify-center px-6 py-4", isFilterShown ? "flex lg:hidden" : "hidden")}><span className="font-inter text-text-primary text-[17px] font-bold">{t("global.filter_by")}</span></div><form className={twMerge("bg-background-secondary mb-5  flex w-full flex-col justify-end gap-3 rounded-b-lg px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0", isFilterShown ? "flex" : "hidden lg:flex")}>{filterData.filterable.map(el => el.shouldRender !== false && el.onRender(tempState[el.field], value => {
          setTempState(prev => {
            if (prev) {
              return {
                ...prev,
                [el.field]: value
              };
            }
          });
        }, tempState, newTempState => setTempState(newTempState)))}<PrimaryButton type="submit" onClick={e => {
          e.preventDefault();
          handleFilter();
        }} className="flex items-center" disabled={!data}><FilterIcon className="h-[15px] w-[15px]" /><span> {t("global.filter")}</span></PrimaryButton></form></div>;
    $[27] = data;
    $[28] = filterData;
    $[29] = handleFilter;
    $[30] = isFilterShown;
    $[31] = setTempState;
    $[32] = t;
    $[33] = tempState;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== children || $[36] !== t7 || $[37] !== t8) {
    t9 = <>{t7}{t8}{children}</>;
    $[35] = children;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
};
const VerticalListingLayout = t0 => {
  const $ = _c(42);
  const {
    title,
    data,
    counter,
    filterData,
    createData,
    children,
    customHeaderElement
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  let t1;
  if ($[0] !== filterData) {
    t1 = {
      filterData
    };
    $[0] = filterData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isFilterShown,
    setIsFilterShown,
    handleFilter,
    setIsDefaultValuesFetching,
    isDefaultValuesFetching,
    tempState,
    setTempState
  } = useListingFilter(t1);
  let t2;
  if ($[2] !== counter || $[3] !== data?.items.length || $[4] !== title) {
    t2 = data?.items.length === 0 || counter === false ? <h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1> : <div className="flex items-center gap-1">{counter ? <h2 className="text-text-primary whitespace-nowrap text-[22px] font-bold">{counter.count}</h2> : <Loader type="dark" small={true} />}<h1 className="flex h-[42px] items-center whitespace-nowrap">{title}</h1></div>;
    $[2] = counter;
    $[3] = data?.items.length;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = (customHeaderElement || createData) && "flex";
  let t4;
  if ($[6] !== t3) {
    t4 = twMerge("flex gap-5 md:gap-3 lg:hidden", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== filterData || $[9] !== isFilterShown || $[10] !== setIsFilterShown) {
    t5 = filterData && <PrimaryButton onClick={() => setIsFilterShown(!isFilterShown)} className="flex items-center lg:hidden" testAttributePostfix={filterData.testAttributePostfix}><FilterIcon className="h-[15px] w-[15px]" /></PrimaryButton>;
    $[8] = filterData;
    $[9] = isFilterShown;
    $[10] = setIsFilterShown;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== customHeaderElement) {
    t6 = customHeaderElement && <div>{customHeaderElement}</div>;
    $[12] = customHeaderElement;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== createData || $[15] !== data || $[16] !== isDefaultValuesFetching || $[17] !== isModalOpen || $[18] !== setIsDefaultValuesFetching || $[19] !== setTempState || $[20] !== t) {
    t7 = createData && <><PrimaryButton disabled={!data} onClick={() => {
        if (createData.onOpen) {
          setIsDefaultValuesFetching(true);
          createData.onOpen().then(() => {
            setIsDefaultValuesFetching(false);
            setIsModalOpen(true);
          });
        } else {
          setIsModalOpen(true);
        }
      }} isFetching={isDefaultValuesFetching} testAttributePostfix={createData.testAttributePostfix}><AddIcon /><span className="hidden sm:flex"> {t("global.create")}</span></PrimaryButton><CreateModal caption={createData.caption} opened={isModalOpen} requiredFields={createData.requiredFields} onClose={() => {
        setIsModalOpen(false);
      }} afterLeave={createData.onAfterLeave} defaultValues={createData.defaultValues} creatable={createData.creatable} onCreate={data_0 => {
        setTempState(undefined);
        return createData.onCreate(data_0);
      }} /></>;
    $[14] = createData;
    $[15] = data;
    $[16] = isDefaultValuesFetching;
    $[17] = isModalOpen;
    $[18] = setIsDefaultValuesFetching;
    $[19] = setTempState;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t2 || $[28] !== t8) {
    t9 = <div className="flex flex-wrap justify-between gap-3 lg:flex-col lg:gap-5">{t2}{t8}</div>;
    $[27] = t2;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== data || $[31] !== filterData || $[32] !== handleFilter || $[33] !== isFilterShown || $[34] !== setTempState || $[35] !== t || $[36] !== tempState) {
    t10 = filterData && tempState && <div className={twMerge("flex w-full flex-col overflow-x-hidden rounded-lg lg:overflow-visible", !isFilterShown && "hidden lg:flex")}><div className={twMerge("bg-brand-primary-subtle justify-center px-6 py-4", isFilterShown ? "flex lg:hidden" : "hidden")}><span className="font-inter text-text-primary text-[17px] font-bold">{t("global.filter_by")}</span></div><form className={twMerge("flex w-full  flex-col justify-end gap-3 rounded-b-lg bg-white px-6 py-4 lg:flex-row lg:bg-transparent lg:px-0 lg:py-0", isFilterShown ? "flex" : "hidden lg:flex")}>{filterData.filterable.map(el => el.shouldRender !== false && el.onRender(tempState[el.field], value => {
          setTempState(prev => {
            if (prev) {
              return {
                ...prev,
                [el.field]: value
              };
            }
          });
        }, tempState, newTempState => setTempState(newTempState)))}<PrimaryButton type="submit" onClick={e => {
          e.preventDefault();
          handleFilter();
        }} className="flex items-center" disabled={!data}><FilterIcon className="h-[15px] w-[15px]" /><span> {t("global.filter")}</span></PrimaryButton></form></div>;
    $[30] = data;
    $[31] = filterData;
    $[32] = handleFilter;
    $[33] = isFilterShown;
    $[34] = setTempState;
    $[35] = t;
    $[36] = tempState;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== children || $[39] !== t10 || $[40] !== t9) {
    t11 = <div className="flex flex-col gap-5">{t9}{t10}{children}</div>;
    $[38] = children;
    $[39] = t10;
    $[40] = t9;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  return t11;
};
export default ListingLayout;